import {
    arrow,
    autoPlacement,
    autoUpdate,
    FloatingArrow,
    FloatingFocusManager,
    FloatingPortal,
    flip,
    Placement,
    ReferenceType,
    safePolygon,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole
} from "@floating-ui/react";
import React, { PropsWithChildren, useRef, useState } from "react";

type PopOverProps = PropsWithChildren<{
    dismissable?: boolean;
    title?: string;
    innerHTML?: string;
    onHover?: boolean;
    onClick?: boolean;
    placement?: Placement;
    anchorComponent: (
        setReference: (node: ReferenceType | null) => void,
        getReferenceProps: (
            userProps?: React.HTMLProps<Element>
        ) => Record<string, unknown>,
        popOverComponent: () => React.JSX.Element
    ) => React.ReactNode;
}>;

const PopOver = ({
    children,
    dismissable = false,
    onClick = true,
    onHover = false,
    placement,
    title,
    innerHTML,
    anchorComponent
}: PopOverProps) => {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const closeTooltip = () => setPopoverOpen(false);

    const arrowRef = useRef(null);

    const middleware = [shift(), arrow({ element: arrowRef })];

    if (!placement) {
        middleware.unshift(flip());
        middleware.unshift(autoPlacement());
    }

    const { refs, floatingStyles, context } = useFloating({
        open: popoverOpen,
        placement,
        onOpenChange: setPopoverOpen,
        whileElementsMounted: autoUpdate,
        middleware
    });

    const focus = useFocus(context);
    const click = useClick(context, { enabled: onClick });
    const hover = useHover(context, {
        enabled: onHover,
        mouseOnly: true,
        handleClose: safePolygon()
    });
    const role = useRole(context);
    const dismiss = useDismiss(context);

    const { getFloatingProps, getReferenceProps } = useInteractions([
        click,
        hover,
        focus,
        role,
        dismiss
    ]);

    const popOverComponent = () => (
        <>
            {popoverOpen && (
                <FloatingPortal>
                    <FloatingFocusManager context={context}>
                        <div
                            role="tooltip"
                            ref={refs.setFloating}
                            style={floatingStyles}
                            className="tooltip-component__wrapper"
                            {...getFloatingProps()}
                        >
                            {dismissable && (
                                <span className="tooltip-component__dismiss">
                                    <i
                                        className={"las la-times"}
                                        onClick={() => closeTooltip()}
                                    />
                                </span>
                            )}
                            {title && (
                                <h3 className="pl-2 tooltip-component__title">
                                    {title}
                                </h3>
                            )}
                            {children && <div className="p-2">{children}</div>}
                            {innerHTML && (
                                <div className="p-2 tooltip-component__body">
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: innerHTML
                                        }}
                                    />
                                </div>
                            )}

                            <FloatingArrow
                                ref={arrowRef}
                                context={context}
                                strokeWidth={1}
                                stroke="#e0e3eb"
                                fill="white"
                                className="tooltip-component__arrow"
                            />
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </>
    );

    return (
        <>
            {anchorComponent(
                refs.setReference,
                getReferenceProps,
                popOverComponent
            )}
        </>
    );
};

export { PopOver };
